/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'

export interface PanelTab<T extends string> {
  key: T
  label: string
  /** Optional count badge, e.g. the number of teams. */
  count?: number
}

interface PanelTabsProps<T extends string> {
  tabs: PanelTab<T>[]
  activeKey: T
  onChange: (key: T) => void
}

/**
 * The detail panel's tab row. Mirrors the roster's Players/Team Officials
 * treatment so a division's Teams/Standings reads identically.
 */
const PanelTabs = <T extends string>({
  tabs,
  activeKey,
  onChange,
}: PanelTabsProps<T>) => {
  'use memo'

  return (
    <div css={rowStyle} role='tablist'>
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey

        return (
          <button
            key={tab.key}
            type='button'
            role='tab'
            aria-selected={isActive}
            css={tabStyle(isActive)}
            onClick={() => onChange(tab.key)}
          >
            {capitalizeWords(tab.label)}
            {typeof tab.count === 'number' && (
              <span css={countStyle(isActive)}>{tab.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

const rowStyle = css`
  display: flex;
  gap: 22px;
  overflow-x: auto;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const tabStyle = (isActive: boolean) => css`
  ${font.body}
  ${paragraphSmall}
  ${isActive ? weightSemiBold : ''}
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 0 10px;
  background: none;
  border: none;
  border-bottom: 2px solid ${isActive ? colors.PRIMARY : 'transparent'};
  color: ${isActive ? colors.NEUTRAL_0 : colors.NEUTRAL_100};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }
`

const countStyle = (isActive: boolean) => css`
  ${font.body}
  ${paragraphXSmall}
  ${weightSemiBold}
  padding: 1px 6px;
  border-radius: 10px;
  font-variant-numeric: tabular-nums;
  background: ${isActive ? colors.PRIMARY : colors.SECONDARY_300};
  color: ${isActive ? colors.PRIMARY_TEXT : colors.NEUTRAL_50};
`

export default PanelTabs
